import { useEffect, useState } from "react";

export interface MultiSelectProps<T> {
  feedbackValid?: string;
  feedbackInvalid?: string;
  validator?: (val: T[]) => {
    valid?: boolean;
    feedback?: string;
    noValidate?: boolean;
  } | void;
  validated?: boolean;
  onFocus?: () => void;
}

const useMultiSelect = <T>(defaultValue: T[], p?: MultiSelectProps<T>) => {
  const [values, setValues] = useState<T[]>(defaultValue);
  const [valid, setValid] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [feedbackValid, setFeedbackValid] = useState(p?.feedbackValid ?? "");
  const [feedbackInvalid, setFeedbackInvalid] = useState(
    p?.feedbackInvalid ?? "",
  );
  const validated = p?.validated ?? false;

  useEffect(() => {
    if (p?.validator) {
      const res = p.validator(values);
      if (res) {
        if (res.noValidate !== undefined) {
          if (res.noValidate) {
            setValid(false);
            setInvalid(false);
            return;
          }
        }
        if (res?.valid) {
          setValid(true);
          setInvalid(false);
          if (res.feedback) {
            setFeedbackValid(res.feedback);
          }
        } else {
          setValid(false);
          setInvalid(true);
          if (res?.feedback) {
            setFeedbackInvalid(res.feedback);
          }
        }
      }
    }
  }, [p, values, validated]);

  const peek = () => {
    return {
      values,
      valid: validated ? valid : false,
      invalid: validated ? invalid : false,
      feedbackValid: feedbackValid,
      feedbackInvalid: feedbackInvalid,
    };
  };

  const onFocus = () => {
    if (p?.onFocus) {
      p.onFocus();
    }
  };

  return [
    {
      values,
      valid: validated ? valid : false,
      invalid: validated ? invalid : false,
      feedbackValid: feedbackValid,
      feedbackInvalid: feedbackInvalid,
    },
    {
      setValues,
      peek,
      onFocus,
      setValid,
      setInvalid,
      setFeedbackValid,
      setFeedbackInvalid,
    },
  ] as const;
};

export default useMultiSelect;
